// src/pages/auth/MfaResetPage.tsx

import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle } from 'lucide-react';
import { authApi } from '@/services/api';
import Button from '@/components/ui/Button';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

const MfaResetPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      return;
    }
    authApi
      .mfaCompleteReset(token)
      .then(() => setStatus('success'))
      .catch(() => setStatus('error'));
  }, [token]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full text-center space-y-8">
        {status === 'loading' && (
          <>
            <LoadingSpinner size="lg" />
            <p className="text-muted-foreground">Resetting your two-factor authentication...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="mx-auto w-16 h-16 bg-success rounded-full flex items-center justify-center shadow-lg">
              <CheckCircle className="w-8 h-8 text-success-foreground" />
            </div>
            <h1 className="text-3xl font-bold text-foreground">MFA Reset Complete</h1>
            <p className="text-muted-foreground">
              Your two-factor authentication has been reset and all sessions have been signed out. Please log in and set up two-factor authentication again.
            </p>
            <Link to="/login">
              <Button className="w-full" size="lg">Sign In</Button>
            </Link>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="mx-auto w-16 h-16 bg-destructive rounded-full flex items-center justify-center shadow-lg">
              <XCircle className="w-8 h-8 text-destructive-foreground" />
            </div>
            <h1 className="text-3xl font-bold text-foreground">Invalid Link</h1>
            <p className="text-muted-foreground">This MFA reset link is invalid or has expired.</p>
            <Link to="/login">
              <Button className="w-full" size="lg">Back to Sign In</Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default MfaResetPage;
